import { createFileRoute } from "@tanstack/react-router";
import { MessageCircle, ArrowUp, FileText, Plus, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ask } from "@/lib/api";
import type { AskCitation } from "@/lib/types";

export const Route = createFileRoute("/_app/askme")({
  component: AskMeView,
});

interface UserMsg {
  id: string;
  role: "user";
  text: string;
  time: string;
}

interface AssistantMsg {
  id: string;
  role: "assistant";
  text: string;
  time: string;
  citations: AskCitation[];
}

interface ErrorMsg {
  id: string;
  role: "error";
  text: string;
  time: string;
}

type Msg = UserMsg | AssistantMsg | ErrorMsg;

const examples = [
  "When does my LIC policy renew?",
  "What was the HDFC overdraft fee last quarter?",
  "Show me my Form 16 from 2024.",
];

function timeNow(): string {
  return new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function CitationChip({ c }: { c: AskCitation }) {
  const inner = (
    <span className="inline-flex items-center gap-1 rounded-md bg-muted hover:bg-muted/80 px-2 py-1 text-xs text-muted-foreground transition">
      <FileText className="h-3 w-3" />
      <span className="max-w-40 truncate">{c.name}</span>
      {c.drive_link && <ExternalLink className="h-3 w-3 opacity-60" />}
    </span>
  );
  if (c.drive_link) {
    return (
      <a
        href={c.drive_link}
        target="_blank"
        rel="noreferrer"
        title={c.snippet}
        aria-label={`Open ${c.name} in Drive`}
      >
        {inner}
      </a>
    );
  }
  return <span title={c.snippet}>{inner}</span>;
}

/**
 * Render answer text with [source:<file_id>] markers replaced by
 * inline-numbered links to the matching citation. The chip list at
 * the bottom keeps the full names; the inline markers stay short.
 */
function renderAnswerWithMarkers(text: string, citations: AskCitation[]) {
  const indexByFileId = new Map<string, number>();
  citations.forEach((c, i) => indexByFileId.set(c.file_id, i + 1));

  const parts: (string | { idx: number; c: AskCitation })[] = [];
  const re = /\[source:([A-Za-z0-9_-]+)\]/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const fid = m[1];
    const idx = indexByFileId.get(fid);
    if (idx) {
      parts.push({ idx, c: citations[idx - 1] });
    } else {
      parts.push(m[0]);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));

  return parts.map((p, i) =>
    typeof p === "string" ? (
      <span key={i}>{p}</span>
    ) : (
      <a
        key={i}
        href={p.c.drive_link ?? "#"}
        target={p.c.drive_link ? "_blank" : undefined}
        rel="noreferrer"
        title={`${p.c.name} — ${p.c.snippet}`}
        className="inline-block align-baseline text-xs font-semibold text-primary hover:underline px-1"
      >
        [{p.idx}]
      </a>
    ),
  );
}

function AskMeView() {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");

  const askMut = useMutation({
    mutationFn: async (question: string) => ask(question),
    onSuccess: (res, question) => {
      // The question already pushed into the list before mutate;
      // append the answer here.
      const t = timeNow();
      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-a`,
          role: "assistant",
          text: res.answer,
          time: t,
          citations: res.citations,
        },
      ]);
      void question; // kept for symmetry; SSE doesn't push assistant msgs
    },
    onError: (e) => {
      const t = timeNow();
      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-e`,
          role: "error",
          text: e instanceof Error ? e.message : "Ask failed",
          time: t,
        },
      ]);
    },
  });

  const send = () => {
    const q = input.trim();
    if (!q || askMut.isPending) return;
    const t = timeNow();
    setMessages((prev) => [
      ...prev,
      { id: `${Date.now()}-q`, role: "user", text: q, time: t },
    ]);
    setInput("");
    askMut.mutate(q);
  };

  const empty = messages.length === 0;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-8 py-8 flex flex-col min-h-[calc(100vh-8rem)]">
      <header className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl">AskMe</h1>
        {!empty && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => setMessages([])}
          >
            <Plus className="h-3.5 w-3.5" /> New chat
          </Button>
        )}
      </header>

      <div className="flex-1 space-y-6 mb-4">
        {empty ? (
          <div className="text-center py-16">
            <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
              <MessageCircle className="h-6 w-6" />
            </div>
            <p className="text-foreground/80 mb-6">
              Ask anything about your library.
            </p>
            <div className="space-y-2 max-w-md mx-auto">
              {examples.map((ex) => (
                <button
                  key={ex}
                  onClick={() => setInput(ex)}
                  className="block w-full text-left rounded-lg border border-border bg-card px-4 py-2.5 text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground transition"
                >
                  "{ex}"
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((m) => (
              <div
                key={m.id}
                className={cn(
                  "flex",
                  m.role === "user" ? "justify-end" : "justify-start",
                )}
              >
                <div
                  className={cn(
                    "max-w-[85%]",
                    m.role === "user" ? "text-right" : "text-left",
                  )}
                >
                  <div className="text-xs text-muted-foreground mb-1">{m.time}</div>
                  <div
                    className={cn(
                      "rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
                      m.role === "user"
                        ? "bg-primary text-primary-foreground rounded-tr-sm"
                        : m.role === "error"
                          ? "bg-destructive/10 text-destructive border border-destructive/30 rounded-tl-sm"
                          : "bg-card border border-border rounded-tl-sm",
                    )}
                  >
                    {m.role === "assistant"
                      ? renderAnswerWithMarkers(m.text, m.citations)
                      : m.text}
                  </div>
                  {m.role === "assistant" && m.citations.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.citations.map((c, i) => (
                        <CitationChip key={i} c={c} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {askMut.isPending && (
              <div className="flex justify-start">
                <div className="max-w-[85%]">
                  <div className="text-xs text-muted-foreground mb-1">…</div>
                  <div className="rounded-2xl px-4 py-2.5 text-sm bg-card border border-border rounded-tl-sm">
                    <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-2" />
                    Searching your library…
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="sticky bottom-20 sm:bottom-4 flex items-center gap-2 rounded-full border border-border bg-card pl-4 pr-1.5 py-1.5 shadow-sm"
      >
        <MessageCircle className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={empty ? "Type a question..." : "Type a follow-up..."}
          className="flex-1 bg-transparent outline-none text-sm py-2"
          disabled={askMut.isPending}
        />
        <button
          type="submit"
          disabled={!input.trim() || askMut.isPending}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-40 hover:opacity-90 transition"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
